<template>
  <b-overlay :show="loading" opacity="0.5">
    <div>
      <list-table 
        :records="apiKeys" 
        :columns="tableColumns"
        :total-records="totalRecords"
        :current-page-number.sync="currentPage"
        :search-filter.sync="searchFilter"
        :show-export-button="false"
        :show-import-button="false"
      >
        <template #action_button>
           <b-button
              variant="primary"
              :to="{ name: 'new-api-keys' }"
              :disabled="loading"
            >
              <span class="text-nowrap">Generate New Key</span>
            </b-button>
        </template>

        <template #filters>
          <b-row>
            <b-col cols="12" md="2" class="">
              <v-select
                class="filter-select"
                placeholder="Status"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :value="statusFilter"
                :options="statusOptions"
                :reduce="val => val.value"
                :disabled="loading"
                @input="(val) => statusFilter = val"
              />
            </b-col>
          </b-row>
        </template>

        <template #cell(name)="data">
          <b-link :to="{ name: 'admin-api-key-usage', params: { api_key_id: data.item._id }}">
            <span>
              {{ getValueFromSource(data.item, 'name') }}
            </span>
          </b-link>
        </template>

        <!-- API Key -->
        <template #cell(secret_key)="data">
          <div class="d-flex justify-content-between align-items-center">
            <div style="max-width: 300px;" class="text-truncate">
              {{ getValueFromSource(data.item, 'secret_key') }}
            </div>
            
            <div v-if="!getValueFromSource(data.item, 'meta.seen', true) && getValueFromSource(data.item, 'status') === 'active'" @click="onViewSecretKey(data.item._id)">
              <feather-icon 
                class="cursor-pointer text-right"
                icon="EyeIcon"
                />
            </div>
          </div>
        </template>

        <template #cell(public_key)="data">
          <div class="d-flex justify-content-between align-items-center">
            <div style="max-width: 300px;" class="text-truncate">
              {{ getValueFromSource(data.item, 'public_key') }}
            </div>

            <div @click="onCopyAPIKey(data.item.public_key)">
              <feather-icon 
                class="cursor-pointer text-right"
                icon="CopyIcon"
                />
            </div>
          </div>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge
            pill
            :variant="`light-${resolveStatusVariant(data.item.status)}`"
            class="text-capitalize"
          >
            {{ capitalizeFirstWord(getLoanStatusTextForAdmin(getValueFromSource(data, 'item.status'))) }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <div class="d-flex cursor-pointer">
            <div v-if="getValueFromSource(data, 'item.status') === 'active'" class="ml-2 cursor-pointer" @click="onDeactivateAPIKey(data.item)">
              <feather-icon 
                icon="Trash2Icon"
                />
            </div>
          </div>
        </template>
      </list-table>
    </div>
  </b-overlay>
</template>

<script>
import {
  BMedia, BAvatar, BButton, VBTooltip,
  BOverlay, BCard, BRow, BCol, BAlert, BLink, BTable,
  BBadge, BDropdown, BDropdownItem, BPagination,
  BFormInput, BInputGroup, BInputGroupAppend
} from 'bootstrap-vue'

import vSelect from 'vue-select'
import { get, debounce } from "lodash"
import { formatDate } from "@/@core/utils/utils"

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ListTable from "@/@core/components/ListTable/ListTable.vue";


const watchHandler = {
  handler(){
    this.debounceFetchAllData()
  }, 
  immediate: false
}

const watchHandlerRequiresPageReset = {
  handler(){
    this.currentPage = 1;
    this.debounceFetchAllData()
  }, 
  immediate: false
}


export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BOverlay,
    BCard,
    BTable,
    BRow,
    BCol,
    BAlert,
    BBadge,
    BLink,
    BMedia,
    BAvatar,
    BDropdown,
    BDropdownItem,
    BPagination,
    BButton,
    BFormInput, 
    BInputGroup, 
    BInputGroupAppend,

    vSelect,
    ListTable
  },
  props: {
    tableColumns: {
      type: Array,
      default: () => {
          return [
          { key: 'name', sortable: false, label: "Name" },
          { key: 'secret_key', sortable: false },
          { key: 'public_key', sortable: false },
          { key: 'status', sortable: false },
          { key: 'created', sortable: false, formatter: val => `${formatDate(val)}`, label: 'Date', },
          { key: 'actions', label: "Action", sortable: false, },
        ]
      }
    }
  },
  data(){
    return {
      loading: false,
      showListTable: false,

      perPageOptions: [50, 100, 150, 250, 500],
      statusFilter: null,
      searchFilter: null,
      apiKeys: [],
      statusOptions: [
        { label: 'Active', value: 'active' },
        { label: 'Expired', value: 'expired' },
        { label: 'Revoked', value: 'revoked' },
      ],

      debounceFetchAllData: null
    }
  },
  computed: {
    dataMeta(){
      const localItemsCount = this.apiKeys.length || 0;
      return {
        from: this.perPage * (this.currentPage - 1) + (localItemsCount ? 1 : 0),
        to: this.perPage * (this.currentPage - 1) + localItemsCount,
        of: this.totalRecords,
      }
    },
    adminPermissions(){
      return get(this.$store.getters[`auth/userData`], 'permissions', {});
    },
    canAddNewAPIKey(){
      return get(this.adminPermissions, 'admin.bank_api.can_add_new', false)
    },
    canDeactivateAPIKey(){
      return get(this.adminPermissions, 'admin.bank_api.can_deactivate', false)
    },
    disableForm(){
      return !this.canAddNewAPIKey
    },
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    perPage: watchHandlerRequiresPageReset,
    statusFilter: watchHandlerRequiresPageReset,
    searchFilter: watchHandlerRequiresPageReset,
    currentPage: watchHandler,
  },
  created(){
    this.debounceFetchAllData = debounce(this.fetchAllData, 500);
    this.fetchAllData();
  },
  methods: {
    onRowClicked(rowItem){
      this.$router.push({ name: 'admin-api-key-usage', params: { api_key_id: rowItem._id } })
      // .catch(() => {})
    },
    onCopyAPIKey(apiKey){
      navigator.clipboard.writeText(apiKey).then(() => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'API key copied',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: "API Key copied successfully."
          },
        });
      })
      .catch(() => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: "There was an error copying api_key. Please copy manually"
          },
        });
      })
    },
    async fetchAllData() {
      try {
        this.loading = true; 

        const query = {
          limit: this.perPage,
          page: this.currentPage,
          search: this.searchFilter,
          status: this.statusFilter
        }

        this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.apiKeys.list(query);
        const { data, pagination } = request.data;
        this.totalRecords = pagination.totalRecords
        this.perPage = pagination.limit;

        this.apiKeys = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
        this.showListTable = true;
      }
    },
    async onViewSecretKey(api_id) {
      try {
        this.loading = true; 

        const request = await this.useJwt().mainService.apiKeys.fetch(api_id);
        const { data } = request.data;
        const record = this.apiKeys.find(opt => opt._id === data._id);
        if (record){
          record.secret_key = data.secret_key;
          record.meta.seen = true;
        } 
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
        this.showListTable = true;
      }
    },
    async onDeactivateAPIKey(bankAPI){
      try { 
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Deactivate?',
          text: "This will deactivate this bank access key.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, deactivate it.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value){
          return;
        }

        const api_key_id = bankAPI._id;
        await this.useJwt().mainService.apiKeys.revoke(api_key_id);
        await this.fetchAllData();
      } catch (error){
        this.loading = false;

        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
  }
}
</script>

<style lang="scss" scoped>
  @import '@core/scss/vue/libs/vue-select.scss';
  .width-100 {
    width: 100px;
  }
  .per-page-selector {
    width: 90px;
  }
</style>

<style lang="scss">
  // .bank-api-key-usage-list-tb-row {
  //   cursor: pointer !important;
  // }
</style>
